import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { api } from '../services/api';

interface ReturnItem {
  sale_id: number;
  customer_name: string;
  product_name: string;
  total_amount: number;
  total_paid: number;
  refund_amount: number;
  reason: string | null;
  return_date: string;
  installments: number;
  installments_paid: number;
}

const fmtCurrency = (n: number) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);

const formatDate = (dateStr: string) => {
  const [y, m, d] = dateStr.split('-');
  return `${d}/${m}/${y}`;
};

export default function ReturnsPage() {
  const [returns, setReturns] = useState<ReturnItem[]>([]);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    api.get('/returns')
      .then((res) => setReturns(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const totalRefunded = returns.reduce((acc, r) => acc + r.refund_amount, 0);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
        Devoluciones
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Chip
          label={`${returns.length} devoluciones`}
          sx={{ fontWeight: 600, fontSize: '0.85rem', backgroundColor: 'rgba(139,92,246,0.15)', color: '#8B5CF6' }}
        />
        <Chip
          label={`Total reembolsado: ${fmtCurrency(totalRefunded)}`}
          sx={{ fontWeight: 600, fontSize: '0.85rem', backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444' }}
        />
      </Box>

      {returns.length === 0 ? (
        <Typography color="text.secondary">No hay devoluciones registradas.</Typography>
      ) : isMobile ? (
        <Grid container spacing={1.5}>
          {returns.map((r) => (
            <Grid item xs={12} key={r.sale_id}>
              <Card sx={{ borderLeft: '4px solid #8B5CF6' }}>
                <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 700 }}>{r.customer_name}</Typography>
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                      {formatDate(r.return_date)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', mb: 1 }}>
                    {r.product_name}
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Cuotas: {r.installments_paid}/{r.installments}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        Pagado: {fmtCurrency(r.total_paid)}
                      </Typography>
                    </Box>
                    {r.refund_amount > 0 && (
                      <Chip
                        label={`Reembolso: ${fmtCurrency(r.refund_amount)}`}
                        size="small"
                        sx={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444', fontWeight: 600 }}
                      />
                    )}
                  </Box>
                  {r.reason && (
                    <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1, fontStyle: 'italic' }}>
                      {r.reason}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cliente</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Producto</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Cuotas</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Venta</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Total Pagado</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Reembolso</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Motivo</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {returns.map((r) => (
                <TableRow key={r.sale_id}>
                  <TableCell>{formatDate(r.return_date)}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{r.customer_name}</TableCell>
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell>{r.installments_paid}/{r.installments}</TableCell>
                  <TableCell>{fmtCurrency(r.total_amount)}</TableCell>
                  <TableCell>{fmtCurrency(r.total_paid)}</TableCell>
                  <TableCell>
                    {r.refund_amount > 0 ? (
                      <Chip
                        label={fmtCurrency(r.refund_amount)}
                        size="small"
                        sx={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#EF4444', fontWeight: 600 }}
                      />
                    ) : (
                      <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', maxWidth: 200 }} noWrap>
                      {r.reason || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
